import * as React from 'react';
import type { ProgrammaticRegionData, ProgrammaticGeoLevel, ProgrammaticPagination } from '../../types/programmatic';
import TrendIndicator from './TrendIndicator';

interface ProgrammaticDataTableProps {
  data: ProgrammaticRegionData[];
  geoLevel: ProgrammaticGeoLevel;
  pagination?: ProgrammaticPagination;
  onPageChange?: (page: number) => void;
  onRowClick?: (region: ProgrammaticRegionData) => void;
  isLoading: boolean;
}

type SortField = 'name' | 'total_jobs' | 'native_apps' | 'js_apps' | 'native_pct' | 'available_profiles' | 'pressure_score' | 'trend_change';
type SortDir = 'asc' | 'desc';

const pressureLabel = (score: number): { text: string; className: string } => {
  if (score < 0.5) return { text: 'Low', className: 'bg-success' };
  if (score <= 2.0) return { text: 'Mod', className: 'bg-warning text-dark' };
  return { text: 'High', className: 'bg-danger' };
};

const ProgrammaticDataTable: React.FC<ProgrammaticDataTableProps> = ({
  data,
  geoLevel,
  pagination,
  onPageChange,
  onRowClick,
  isLoading,
}) => {
  const [sortField, setSortField] = React.useState<SortField>('native_pct');
  const [sortDir, setSortDir] = React.useState<SortDir>('desc');
  const [search, setSearch] = React.useState('');

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return <i className="fas fa-sort text-muted ms-1" style={{ fontSize: '0.7rem' }} />;
    return sortDir === 'asc'
      ? <i className="fas fa-sort-up text-primary ms-1" style={{ fontSize: '0.7rem' }} />
      : <i className="fas fa-sort-down text-primary ms-1" style={{ fontSize: '0.7rem' }} />;
  };

  const filtered = React.useMemo(() => {
    let result = [...data];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((r) => r.name.toLowerCase().includes(q));
    }
    result.sort((a, b) => {
      const aVal = a[sortField] as number;
      const bVal = b[sortField] as number;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? (aVal || 0) - (bVal || 0) : (bVal || 0) - (aVal || 0);
    });
    return result;
  }, [data, search, sortField, sortDir]);

  const regionLabel = geoLevel === 'states' ? 'State' : geoLevel === 'cities' ? 'City' : 'Zip Code';

  if (isLoading) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary" />
          <span className="ms-2 text-muted">Loading table data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="fas fa-table me-2"></i>
          Supply Data by {regionLabel}
        </h6>
        <div className="d-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder={`Search ${regionLabel.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 180 }}
          />
          <small className="text-muted">{filtered.length} rows</small>
        </div>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th role="button" onClick={() => toggleSort('name')} style={{ minWidth: 100 }}>
                  {regionLabel} {sortIcon('name')}
                </th>
                <th role="button" className="text-end" onClick={() => toggleSort('total_jobs')}>
                  Jobs {sortIcon('total_jobs')}
                </th>
                <th role="button" className="text-end" onClick={() => toggleSort('native_apps')}>
                  Native {sortIcon('native_apps')}
                </th>
                <th role="button" className="text-end" onClick={() => toggleSort('js_apps')}>
                  JS/Paid {sortIcon('js_apps')}
                </th>
                <th role="button" className="text-end" onClick={() => toggleSort('native_pct')}>
                  Native % {sortIcon('native_pct')}
                </th>
                <th role="button" className="text-end" onClick={() => toggleSort('available_profiles')}>
                  Profiles {sortIcon('available_profiles')}
                </th>
                <th role="button" className="text-end" onClick={() => toggleSort('pressure_score')}>
                  Pressure {sortIcon('pressure_score')}
                </th>
                <th role="button" className="text-end" onClick={() => toggleSort('trend_change')}>
                  Trend {sortIcon('trend_change')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No data available for the selected filters.
                  </td>
                </tr>
              ) : (
                filtered.map((row, idx) => {
                  const pressure = pressureLabel(row.pressure_score);
                  return (
                    <tr
                      key={`${row.name}-${idx}`}
                      role={onRowClick ? 'button' : undefined}
                      onClick={() => onRowClick?.(row)}
                      style={onRowClick ? { cursor: 'pointer' } : undefined}
                    >
                      <td className="fw-semibold">{row.name}</td>
                      <td className="text-end">{row.total_jobs.toLocaleString()}</td>
                      <td className="text-end">{row.native_apps.toLocaleString()}</td>
                      <td className="text-end">{row.js_apps.toLocaleString()}</td>
                      <td className="text-end">
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          <div
                            style={{
                              width: 40,
                              height: 6,
                              backgroundColor: '#e9ecef',
                              borderRadius: 3,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(row.native_pct, 100)}%`,
                                height: '100%',
                                backgroundColor: row.native_pct >= 60 ? '#198754' : row.native_pct >= 30 ? '#ffc107' : '#dc3545',
                                borderRadius: 3,
                              }}
                            />
                          </div>
                          <span>{row.native_pct}%</span>
                        </div>
                      </td>
                      <td className="text-end">{row.available_profiles.toLocaleString()}</td>
                      <td className="text-end">
                        <span className={`badge ${pressure.className}`} style={{ fontSize: '0.7rem' }}>
                          {pressure.text}
                        </span>
                        <small className="ms-1 text-muted">{row.pressure_score}</small>
                      </td>
                      <td className="text-end">
                        <TrendIndicator change={row.trend_change} size="sm" />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
            <small className="text-muted">
              Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_count} total)
            </small>
            <div className="btn-group btn-group-sm">
              <button
                className="btn btn-outline-secondary"
                disabled={!pagination.has_prev_page}
                onClick={() => onPageChange?.(pagination.current_page - 1)}
              >
                <i className="fas fa-chevron-left" />
              </button>
              <button
                className="btn btn-outline-secondary"
                disabled={!pagination.has_next_page}
                onClick={() => onPageChange?.(pagination.current_page + 1)}
              >
                <i className="fas fa-chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ProgrammaticDataTable.displayName = 'ProgrammaticDataTable';
export default ProgrammaticDataTable;
